import React, { useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useHistory } from 'react-router';
import { DNABox, DNAButton, DNAText } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useCRMStatus } from 'src/hooks/useCRMStatus';
import WidgetContainer from '../WidgetContainer';
import withPermissionsAccessControl from 'src/hooks/useFeatureFlags/withPermissionsAccessControl';

const styles = StyleSheet.create({
  actionRowTitle: {
    fontSize: 15,
    fontWeight: '600',
  },
  actionRow: {
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors['color-gray-50'],
  },
});

type ActionRowProps = {
  title: string,
  description: string,
  actionButtonText: string,
  actionButtonFunction: () => void,
}

const ActionRow: React.FC<ActionRowProps> = (props) => {
  const { title, description, actionButtonText, actionButtonFunction } = props;

  return (
    <DNABox fill appearance="col" spacing="md" style={styles.actionRow}>
      <DNAText style={styles.actionRowTitle}>{title}</DNAText>
      <DNAText status="neutral">{description}</DNAText>
      <DNAButton
        appearance="outline"
        children={actionButtonText}
        onPress={actionButtonFunction}
      />
    </DNABox>
  )
};

const RecommendedActionsBase: React.FC = () => {
  const history = useHistory();
  const { displayDashboardCRMSection } = useCRMStatus();
  const { isOfflineEnabled, isOnline } = useAppSettings();
  const [displayActions, setDisplayActions] = useState(false);

  const availableActionCount = useMemo(() =>
    [displayDashboardCRMSection, !isOfflineEnabled].filter(displayAction => displayAction).length,
  [displayDashboardCRMSection, isOfflineEnabled]);

  const handleConnectToCRM = () => {
    history.push('/profile/crm-integration');
  };

  const handleActivateOfflineMode = () => {
    history.push('/profile/offline');
  };

  if (!isOnline || !availableActionCount) return null;
  // [NOTE]: Avoid using Iffy here, as a null child will render extra spacing.
  return (
    <WidgetContainer
      title="Recommended Actions"
      actionsCount={availableActionCount}
      headerRightSideComponent={<DNAButton
        appearance="ghost"
        size="sm"
        children={displayActions ? 'Hide' : 'View'}
        onPress={() => setDisplayActions(p => !p)}
      />}
    >
      {displayActions &&
        <DNABox fill appearance="col" spacing="md">
          {
            displayDashboardCRMSection && (
              <ActionRow
                title="Connect to CRM"
                // eslint-disable-next-line max-len
                description="Your organization has enabled CRM integration. Please log into your CRM to sync the latest data."
                actionButtonText="Connect"
                actionButtonFunction={handleConnectToCRM}
              />
            )
          }
          {
            !isOfflineEnabled && (
              <ActionRow
                title="Activate Offline Mode"
                description="Install Beacon for offline use on your computer as an application."
                actionButtonText="Activate"
                actionButtonFunction={handleActivateOfflineMode}
              />
            )
          }
        </DNABox>
      }
    </WidgetContainer>
  );
};

const RecommendedActions = withPermissionsAccessControl(RecommendedActionsBase, {
  flags: ['enableHomeRecommendedActionsWidget'],
});

export default RecommendedActions;
